import * as React from "react";

/**
 * Inline markdown renderer — small, sandboxed, no external deps.
 *
 * Supports:
 *   **bold** → <strong>
 *   *italic* / _italic_ → <em>
 *   `inline code` → <code>
 *   [link text](https://...) → <a target="_blank" rel="noopener">
 *   ```code blocks``` → <pre>
 *   - / 1. lists at line start → <li>
 *   > quotes at line start → <blockquote>
 *
 * Does NOT support: HTML pass-through (XSS-safe), images, tables, headings.
 * That's intentional — comment threads don't need them, and we avoid the
 * audit overhead of bringing in react-markdown / remark / dompurify.
 *
 * If we later need a richer renderer (e.g. for client_narrative_pdf_pipeline
 * outputs), swap to react-markdown + rehype-sanitize behind the same Markdown
 * component prop API.
 */

type Token =
  | { kind: "text"; value: string }
  | { kind: "bold"; children: Token[] }
  | { kind: "italic"; children: Token[] }
  | { kind: "code"; value: string }
  | { kind: "link"; href: string; children: Token[] };

const MD_PATTERNS: Array<[RegExp, (m: RegExpExecArray) => Token]> = [
  // ```code block``` — multi-line; not handled here (handled at line level)
  // **bold**
  [/\*\*([^*]+)\*\*/g, (m) => ({ kind: "bold", children: tokenizeInline(m[1]) })],
  // *italic* OR _italic_
  [/(?<!\*)\*([^*\n]+)\*(?!\*)/g, (m) => ({ kind: "italic", children: tokenizeInline(m[1]) })],
  [/(?<![a-zA-Z0-9_])_([^_\n]+)_(?![a-zA-Z0-9_])/g, (m) => ({ kind: "italic", children: tokenizeInline(m[1]) })],
  // `inline code`
  [/`([^`\n]+)`/g, (m) => ({ kind: "code", value: m[1] })],
  // [text](url)
  [
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (m) => ({ kind: "link", href: m[2], children: tokenizeInline(m[1]) }),
  ],
];

function tokenizeInline(s: string): Token[] {
  const tokens: Token[] = [];
  let cursor = 0;
  while (cursor < s.length) {
    let earliest: { pat: RegExpExecArray; idx: number; tokenizer: (m: RegExpExecArray) => Token } | null = null;
    for (const [re, tokenizer] of MD_PATTERNS) {
      re.lastIndex = cursor;
      const m = re.exec(s);
      if (m && (earliest === null || m.index < earliest.idx)) {
        earliest = { pat: m, idx: m.index, tokenizer };
      }
    }
    if (!earliest) {
      tokens.push({ kind: "text", value: s.slice(cursor) });
      break;
    }
    if (earliest.idx > cursor) {
      tokens.push({ kind: "text", value: s.slice(cursor, earliest.idx) });
    }
    tokens.push(earliest.tokenizer(earliest.pat));
    cursor = earliest.idx + earliest.pat[0].length;
  }
  return tokens;
}

function renderTokens(tokens: Token[], keyPrefix = ""): React.ReactNode[] {
  return tokens.map((t, i) => {
    const k = `${keyPrefix}${i}`;
    switch (t.kind) {
      case "text":
        return <React.Fragment key={k}>{t.value}</React.Fragment>;
      case "bold":
        return (
          <strong key={k} className="font-semibold">
            {renderTokens(t.children, k + "b")}
          </strong>
        );
      case "italic":
        return (
          <em key={k} className="italic">
            {renderTokens(t.children, k + "i")}
          </em>
        );
      case "code":
        return (
          <code key={k} className="px-1 py-0.5 rounded bg-slate-800 text-accent text-[11px] font-mono">
            {t.value}
          </code>
        );
      case "link":
        return (
          <a
            key={k}
            href={t.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline break-all"
          >
            {renderTokens(t.children, k + "l")}
          </a>
        );
    }
  });
}

/**
 * Markdown — render a plain-text body with safe inline markdown formatting.
 *
 * @param children body_md string (raw text)
 * @param className applied to the wrapping div
 */
export function Markdown({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  if (!children) return null;
  // Split into blocks: code-fence ``` blocks vs everything else
  const blocks: Array<{ kind: "fence"; value: string } | { kind: "block"; lines: string[] }> = [];
  const lines = children.split("\n");
  let inFence = false;
  let fenceBuf: string[] = [];
  let blockBuf: string[] = [];
  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      if (inFence) {
        // close
        blocks.push({ kind: "fence", value: fenceBuf.join("\n") });
        fenceBuf = [];
        inFence = false;
      } else {
        // open
        if (blockBuf.length > 0) {
          blocks.push({ kind: "block", lines: blockBuf });
          blockBuf = [];
        }
        inFence = true;
      }
      continue;
    }
    if (inFence) fenceBuf.push(line);
    else blockBuf.push(line);
  }
  if (blockBuf.length > 0) blocks.push({ kind: "block", lines: blockBuf });
  if (fenceBuf.length > 0) blocks.push({ kind: "fence", value: fenceBuf.join("\n") });

  return (
    <div className={className}>
      {blocks.map((b, bi) => {
        if (b.kind === "fence") {
          return (
            <pre
              key={bi}
              className="my-1.5 px-2 py-1 rounded bg-slate-800/80 text-[11px] font-mono overflow-x-auto whitespace-pre"
            >
              {b.value}
            </pre>
          );
        }
        return (
          <React.Fragment key={bi}>
            {b.lines.map((line, li) => {
              const trimmed = line.trim();
              if (trimmed === "") return <br key={`${bi}-${li}`} />;
              if (trimmed.startsWith("> ")) {
                return (
                  <blockquote
                    key={`${bi}-${li}`}
                    className="border-l-2 border-slate-700 pl-2 text-slate-400 italic"
                  >
                    {renderTokens(tokenizeInline(trimmed.slice(2)))}
                  </blockquote>
                );
              }
              const ulMatch = /^- (.+)$/.exec(trimmed);
              if (ulMatch) {
                return (
                  <div key={`${bi}-${li}`} className="flex gap-1.5">
                    <span className="text-slate-600 flex-shrink-0">•</span>
                    <span>{renderTokens(tokenizeInline(ulMatch[1]))}</span>
                  </div>
                );
              }
              const olMatch = /^(\d+)\.\s+(.+)$/.exec(trimmed);
              if (olMatch) {
                return (
                  <div key={`${bi}-${li}`} className="flex gap-1.5">
                    <span className="text-slate-600 tabular-nums flex-shrink-0">{olMatch[1]}.</span>
                    <span>{renderTokens(tokenizeInline(olMatch[2]))}</span>
                  </div>
                );
              }
              return (
                <div key={`${bi}-${li}`}>{renderTokens(tokenizeInline(line))}</div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
}
